import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { isAfter } from "date-fns";
import {
  Button,
  Divider,
  Header,
  Icon,
  Label,
  Message,
  Popup,
  Segment,
} from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { MarkdownViewer, RichTextViewer } from "simplydo/react/richtext";

import { useAppSelector } from "store";
import util from "utils/utils";

import { SideNavContainer } from "components/challenges/YourIdeas";
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import CircularProgress from "components/lib/CircularProgress";
import IPStatementInfo from "components/lib/IPStatementInfo";
import { SubmitIdeaButton } from "components/lib/Ideas";
import IdeaImpactStatement from "components/lib/Ideas/ImpactStatements/IdeaImpactStatement";
import { SideNavLink } from "components/lib/SideNav";
import {
  BasicAccordion,
  PlaceholderItem,
  PlaceholderStack,
  Sticky,
  TooltipButton,
} from "components/lib/UI";

import IdeaAttachments from "../Attachments";
import IdeaComments from "../Comments";
import IdeaBusinessProfile from "../Components/IdeaBusinessProfile";
import IdeaSectionHeader from "../SectionHeader.jsx";
import useScrollTracking, { type ItemRefs } from "../useIdeaScrollTracking";
import { AssessorToolbar } from "./AssessorToolbar";
import Custom from "./Custom";
import IdeaNameField from "./Name";

const IdeaSection = styled.div<{ $isAssessment?: boolean }>`
  border-radius: 8px 8px 2px 2px;
  box-shadow: 0px 5px 10px 0px rgba(0, 0, 0, 0.1);
  @media print {
    page-break-after: always;
    margin: 2cm 30px;
  }

  ${({ $isAssessment, theme }) =>
    $isAssessment ?
      `
    border: 4px solid ${theme.colorMap.violet};
    border-bottom-width: 3px;
    border-top-width: 3px;
    
    .ui.segment {
      border: none !important;
    }  
  `
    : ""};
`;

const ChildIdeaChooser = styled.div`
  z-index: 999;
  padding: 5px;
  border-radius: 5px;
  background-color: white;
  box-shadow: 0px 10px 10px rgba(0, 0, 0, 0.1);
  border: 0.5px solid #e9ebee;
  > .title {
    opacity: 0.8;
    display: block;
    margin: 0 5px;
    font-weight: bold;
    margin-bottom: 5px;
  }
  > .buttons {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    gap: 5px;
    .ui.button.merge-button-tab {
      width: 250px;
    }
    .button-idea-name {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const StickyMergeIdeaHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
  align-items: center;
  .jump-to-section-button {
    margin-top: 5px;
    height: 25px;
    padding: 5px 12.5px;
  }
  ${ChildIdeaChooser} {
    width: 100%;
  }
`;

export type ChallengeIdea = OpenAPI.Schemas["ResponseGetIdea"];

type BodyProps = {
  idea: ChallengeIdea;
  setSectionRef?: (ref: HTMLDivElement | null, key: string) => void;
  sectionRefs?: RefObject<ItemRefs>;
  setFieldRef?: (ref: HTMLDivElement | null, key: string) => void;
  fieldRefs?: RefObject<ItemRefs>;
  challenge?: OpenAPI.GET<"/challenges/{id}">["response"];
  loading?: boolean;
  updateIdea: (value: any, path: string[], replace?: boolean) => void;
  scrollToRequired?: () => void;
  onSubmit?: () => void;
  isEditing?: boolean;
  isSaving?: boolean;
  hideComments?: boolean;
  externalChanges?: any;
  isViewingMergedIdea?: boolean;
  unsaved?: boolean;
  scrollToSection?: (sectionId: string) => void;
  emptyRequiredField?: boolean;
};

type BodyContainerProps = BodyProps & {
  scrollRef?: RefObject<HTMLDivElement | null>;
  removeIdeaFromMerge?: (parentId: string, childId: string) => void;
  removingIdeaFromMerge?: boolean;
};

const BodyContainer = ({
  idea,
  scrollRef,
  removeIdeaFromMerge,
  removingIdeaFromMerge,
  setSectionRef,
  sectionRefs,
  setFieldRef,
  fieldRefs,
  ...rest
}: BodyContainerProps) => {
  /*
    Code for handling merged idea bodies

    One idea is set as the "active" idea, and the content from that idea will be what is shown
  */

  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const sectionScroll = useScrollTracking(sectionRefs, scrollRef, -180);
  const fieldScroll = useScrollTracking(fieldRefs, scrollRef, -180);

  const usingMergedIdea = useMemo(
    () => !!idea?.children?.length,
    [idea.children],
  );

  const [activeChildIdea, setActiveChildIdea] = useState<OpenAPI.Assign<
    OpenAPI.Schemas["Idea"],
    OpenAPI.Schemas["AssignIdeaTemplates"],
    OpenAPI.Schemas["AssignIdeaAuthors"],
    OpenAPI.Schemas["AssignIdeaChallenges"]
  > | null>(null);
  const [prevSectionViewed, setPrevSectionViewed] = useState<string>("");

  useEffect(() => {
    if (
      idea?.ownerChildren &&
      idea.ownerChildren.length > 0 &&
      !activeChildIdea
    ) {
      setActiveChildIdea(idea.ownerChildren[0]);
    }
  }, [activeChildIdea, idea.ownerChildren]);

  const changeActiveChildIdea = useCallback(
    (childIdea: OpenAPI.Schemas["Idea"]) => {
      if (scrollRef?.current) {
        scrollRef.current.scrollTop = 0;
      } else {
        window.scrollTo(0, 0);
      }
      setPrevSectionViewed(sectionScroll.activeItemId ?? "");
      setActiveChildIdea(childIdea);
    },
    [sectionScroll.activeItemId, scrollRef],
  );

  // You can jump to the section that you were viewing in the previous idea, if the section exists in the new idea, and it's not the first section
  const canJumpToSectionInIdea = useMemo(() => {
    if (activeChildIdea === null || prevSectionViewed === "") {
      return false;
    }
    const sectionIds = Object.keys(sectionRefs?.current ?? {});
    const prevSectionIndex = sectionIds.findIndex(
      (sectionId) => sectionId === prevSectionViewed,
    );
    return (
      sectionIds.length > 0 &&
      sectionIds.indexOf(prevSectionViewed) !== -1 &&
      prevSectionIndex !== 0
    );
  }, [activeChildIdea, prevSectionViewed, sectionRefs]);

  const onScrollToPrevSection = useCallback(
    (sectionId: string) => {
      setPrevSectionViewed("");
      sectionScroll.scrollToItem(sectionId);
    },
    [sectionScroll],
  );

  const usingIdea = useMemo(
    () => (usingMergedIdea ? activeChildIdea : idea),
    [usingMergedIdea, activeChildIdea, idea],
  );

  const prevSectionName = useMemo(
    () =>
      activeChildIdea?.ownerIdeaTemplate?.body?.find(
        (section) => section.id === prevSectionViewed,
      )?.name,
    [activeChildIdea?.ownerIdeaTemplate?.body, prevSectionViewed],
  );

  if (usingMergedIdea) {
    if (activeChildIdea === null) {
      return null;
    }
    return (
      <div>
        <Sticky
          top={window.location.pathname === `/ideas/${idea._id}` ? 110 : 5}
          bottom={undefined}
          style={{ zIndex: 100 }}
        >
          <StickyMergeIdeaHeader>
            <ChildIdeaChooser>
              <span className="title">Viewing merged idea...</span>
              <div className="buttons">
                {idea.ownerChildren?.map((childIdea) => (
                  <Button.Group key={childIdea._id}>
                    <Button
                      active={childIdea._id === activeChildIdea?._id}
                      key={childIdea._id}
                      onClick={() => changeActiveChildIdea(childIdea)}
                      className="merge-button-tab"
                    >
                      <Button.Content>
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <span className="button-idea-name">
                            {childIdea.name}
                          </span>
                          {childIdea?.owner ?
                            <span style={{ opacity: 0.5, fontSize: "0.8em" }}>
                              By {childIdea?.owner?.profile?.fullName}
                            </span>
                          : null}
                        </div>
                      </Button.Content>
                    </Button>
                    {(
                      (util.canManageChallenge(user, idea.ownerChallenge) ||
                        util.hasPermission(
                          user,
                          "challenge.mergeIdeas",
                          idea.challenge,
                        )) &&
                      removeIdeaFromMerge
                    ) ?
                      <Popup
                        on="hover"
                        trigger={
                          <Button
                            icon="trash"
                            onClick={() =>
                              removeIdeaFromMerge(idea._id, childIdea._id)
                            }
                          />
                        }
                        content={`Remove this ${t("generic.idea")} from the merge`}
                      />
                    : null}
                  </Button.Group>
                ))}
              </div>
              {idea.ownerChildren?.length !== idea.children?.length ?
                <Message info size="tiny">
                  You are not able to view all {t("generic.ideas")} in this
                  merge. You may not have permission to view some of the{" "}
                  {t("generic.ideas")}, or they may have been deleted.
                </Message>
              : null}
            </ChildIdeaChooser>
            {(
              canJumpToSectionInIdea &&
              (scrollRef ?
                scrollRef.current.scrollTop < 100
              : window.scrollY < 100)
            ) ?
              <div>
                <Button
                  icon="arrow circle down"
                  content={`Jump to ${!prevSectionName ? "previous section" : `section "${prevSectionName}"`}`}
                  onClick={() => onScrollToPrevSection(prevSectionViewed)}
                  className="jump-to-section-button"
                  size="tiny"
                />
              </div>
            : null}
          </StickyMergeIdeaHeader>
        </Sticky>
        {(
          activeChildIdea &&
          (activeChildIdea?.user === user?._id ||
            activeChildIdea?.collaborators?.indexOf(user?._id) !== -1) &&
          !(idea.user === user?._id)
        ) ?
          <Message info>
            Your {t("generic.idea")} has been merged with other{" "}
            {t("generic.ideas")}; You are currently viewing the merge result.
            You can still view your individual {t("generic.idea")}{" "}
            <Link to={`/ideas/${activeChildIdea._id}`}>here</Link>.
          </Message>
        : null}
        <Body
          idea={usingIdea}
          setSectionRef={setSectionRef}
          setFieldRef={setFieldRef}
          isViewingMergedIdea
          scrollToSection={sectionScroll.scrollToItem}
          scrollToField={fieldScroll.scrollToItem}
          {...rest}
        />
        {!removingIdeaFromMerge ?
          <div>
            <div ref={(ref) => setSectionRef?.(ref, "comments")} id="comments">
              <Divider hidden />
              <Header as="h4">Comments</Header>
              <IdeaComments idea={idea} forId={idea._id} isViewingMergedIdea />
              <Divider hidden />
              <Divider hidden />
            </div>
          </div>
        : null}
      </div>
    );
  }

  return (
    <>
      {rest.loading || !usingIdea || !usingIdea._id ? null : (
        <AssessorToolbar
          isEditing={rest.isEditing}
          idea={usingIdea}
          activeItemId={fieldScroll.activeItemId}
        />
      )}
      <Body
        idea={usingIdea}
        setSectionRef={setSectionRef}
        setFieldRef={setFieldRef}
        scrollToSection={sectionScroll.scrollToItem}
        {...rest}
      />
    </>
  );
};

const Body = (props: BodyProps) => {
  const {
    idea,
    challenge,
    loading,
    setSectionRef,
    setFieldRef,
    updateIdea,
    scrollToRequired,
    onSubmit,
    isEditing,
    isSaving,
    unsaved,
    hideComments,
    externalChanges,
    isViewingMergedIdea,
    scrollToSection,
    scrollToField,
  } = props;
  const user = useAppSelector((state) => state.user);
  const theme = useTheme();
  const { t } = useTranslation();
  const firstAssessmentSectionIndex = idea?.ownerIdeaTemplate?.body?.findIndex(
    (section) => section.type === "assessment",
  );

  const {
    isAssessor,
    selectedAssessmentRoundId,
    urlAssessmentRoundId,
    assessmentState,
    submitAssessment,
    assessmentIsSubmissable,
    isSavingAssessment,
  } = useAssessmentEditor(idea);
  const hasCompletedConflictDeclaration =
    assessmentState?.conflictOfInterest?.hasConflictOfInterest !== undefined;

  const {
    parent,
    ownerIdeaTemplate,
    attachments = [],
    commentCount = 0,
    ownerChallenge = {
      preventIdeaComments: false,
      ideaAnonymity: "",
      stage: "",
    },
  } = idea;

  const canEdit = util.canEditIdea(user, idea);
  const isAuthor = util.isIdeaAuthor(user, idea);
  const canViewEdi = util.hasPermission(
    user,
    "org.viewEdi",
    idea?.ownerChallenge?.organisation,
  );
  const showAttachments =
    ownerIdeaTemplate?.enableAttachments &&
    ((canEdit && isEditing) || attachments.length > 0);
  const showComments =
    (!isEditing || commentCount) &&
    !ownerChallenge?.preventIdeaComments &&
    !hideComments &&
    idea._id &&
    (!isViewingMergedIdea || (isViewingMergedIdea && !idea.parent));

  const assessmentFeedback = useMemo(() => {
    const items = ((idea as any)?.assessmentFeedback || []) as Array<any>;
    if (!items.length) return [];

    const rounds = idea?.ownerIdeaTemplate?.assessments?.rounds || [];
    const roundNameById = new Map(rounds.map((r) => [r.id, r.name]));

    const orderedRoundIds = rounds.map((r) => r.id);
    const extraRoundIds = Array.from(
      new Set(items.map((i) => i.assessmentRoundId).filter(Boolean)),
    ).filter((id) => !orderedRoundIds.includes(id));

    const allRoundIds = [...orderedRoundIds, ...extraRoundIds];
    return allRoundIds
      .map((roundId) => {
        const entry = items.find((i) => i.assessmentRoundId === roundId);
        if (!entry) return null;
        return {
          roundId,
          roundName: roundNameById.get(roundId) || "Assessment feedback",
          entry,
        };
      })
      .filter(Boolean) as Array<{
      roundId: string;
      roundName: string;
      entry: any;
    }>;
  }, [idea]);

  return (
    <>
      {isAuthor && assessmentFeedback.length ?
        <div>
          {assessmentFeedback.map(({ roundId, roundName, entry }) => (
            <Message key={roundId} style={{ marginBottom: 24 }}>
              <BasicAccordion
                title={
                  <Message.Header>
                    {t("common:capitalise", { key: "generic.idea" })} assessment
                    feedback - {roundName}
                  </Message.Header>
                }
                content={
                  <>
                    <Divider fitted style={{ marginBottom: "0.75em" }} />
                    <RichTextViewer value={entry.feedback || ""} />
                  </>
                }
              />
            </Message>
          ))}
        </div>
      : null}
      {idea?.ipStatement?.ipStatementEnabled ?
        <IPStatementInfo ipStatement={idea.ipStatement} />
      : null}
      {(
        util.hasPermission(user, "super.viewDashboard") &&
        idea?.generalSummary &&
        idea?.ownerChallenge?.aiIdeaSummarisation
      ) ?
        <Message
          size="small"
          style={{
            boxShadow:
              "0px 5px 10px 0px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(34,36,38,.22) inset",
          }}
        >
          <Message.Header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            Automated {t("generic.idea")} summary
            <Popup
              offset={[13, 2]}
              trigger={
                <Icon
                  name="question circle"
                  size="small"
                  style={{ color: "#666" }}
                />
              }
              content={`This is an automated summary of this ${t("generic.idea")} based on its own content and the ${t("generic.challenge")} brief. It does not represent an assessment or critique.`}
            />
          </Message.Header>
          <Divider fitted style={{ marginTop: 4, marginBottom: 4 }} />

          <MarkdownViewer value={`"${idea.generalSummary}"`} />
        </Message>
      : null}

      {ownerChallenge?.ideaAnonymity && canEdit ?
        <Message
          info
          icon="user secret"
          header="You are working anonymously"
          content={`${t("common:capitalise", { key: "generic.ideas" })} in this ${t("generic.challenge")} have their identity hidden to anyone who is not ${t("generic.challengeWithArticle")} administrator. Only you and ${t("generic.challenge")} administrators will be able to see that you created this ${t("generic.idea")}.`}
        />
      : null}

      {(
        !idea.isSubmitted &&
        canEdit &&
        idea.submissionDeadline &&
        isAfter(new Date(idea.submissionDeadline.deadline), new Date())
      ) ?
        <Message
          info
          icon="calendar"
          header={`${t("common:capitalise", { key: "generic.idea" })} deadline`}
          content={
            <>
              <p>
                This {t("generic.idea")} can be submitted until{" "}
                {formatDate(idea.submissionDeadline.deadline)}. After this time,
                you will not be able to submit this {t("generic.idea")}.
              </p>
              {idea.submissionDeadline.note ?
                <p>
                  The {t("generic.challenge")} manager left the following
                  message: "{idea.submissionDeadline.note}"
                </p>
              : null}
            </>
          }
        />
      : null}

      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
          <IdeaSection>
            <Segment>
              <PlaceholderStack gap={20}>
                <PlaceholderItem height={30} fluid />
                <PlaceholderItem height={150} fluid />
              </PlaceholderStack>
            </Segment>
          </IdeaSection>

          {Array(3)
            .fill(0)
            .map((_, index) => (
              <IdeaSection key={index}>
                <Segment attached="top" style={{ margin: 0 }}>
                  <PlaceholderItem height={30} fluid />
                </Segment>
                <Segment attached="bottom" style={{ margin: 0 }}>
                  <PlaceholderStack gap={10}>
                    <PlaceholderItem height={40} fluid />
                    <PlaceholderItem height={40} fluid />
                    <PlaceholderItem height={40} fluid />
                  </PlaceholderStack>
                </Segment>
              </IdeaSection>
            ))}
        </div>
      )}

      <IdeaNameField
        isEditing={isEditing}
        ideaId={idea?._id}
        ideaName={idea?.name}
        // @ts-ignore
        ideaCoverImage={util.ideaCoverImage(idea, "2560x400")}
        challengeName={challenge?.name}
        updateIdea={updateIdea}
        challenge={idea?.ownerChallenge}
      />
      {ownerIdeaTemplate?.body?.map(
        (
          section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
          sectionIndex: number,
        ) => {
          if (!util.shouldShowIdeaSection(idea, section)) return null;
          if (
            section.type === "edi" &&
            !util.shouldShowIdeaEdiSection(user, idea, section)
          ) {
            return null;
          }
          if (section.type === "assessment") {
            if (
              isAssessor &&
              (!assessmentState ||
                !hasCompletedConflictDeclaration ||
                assessmentState?.conflictOfInterest?.hasConflictOfInterest)
            ) {
              return null;
            }
            if (!isAssessor && !urlAssessmentRoundId) {
              return null;
            }

            const sectionAssessmentRoundId =
              section.assessmentRoundId || "legacy";
            if (
              sectionAssessmentRoundId !==
              (selectedAssessmentRoundId || "legacy")
            ) {
              return null;
            }
          }
          if (section.type === "impact") {
            return (
              <>
                <IdeaSection
                  ref={(ref) => setSectionRef?.(ref, "impactStatement")}
                  style={{
                    boxShadow:
                      (
                        isEditing &&
                        idea?.submissibilityStatus?.detail?.impactStatement
                          ?.incorrectFields?.length
                      ) ?
                        "0.5px 0.5px 5px rgb(233 11 11 / 94%)"
                      : undefined,
                    borderRadius: 5,
                  }}
                >
                  <IdeaSectionHeader
                    isAssessor={isAssessor}
                    canViewEdi={false}
                    canEdit={false}
                    isAuthor={false}
                    section={{
                      id: "impactStatement",
                      name: (
                        <>
                          {section?.name}
                          {(
                            isEditing &&
                            challenge?.impactStatements?.requireOnIdea
                          ) ?
                            <Icon
                              name="asterisk"
                              style={{
                                position: "relative",
                                top: -5,
                                marginLeft: 3,
                              }}
                              color="red"
                              size="tiny"
                            />
                          : null}
                        </>
                      ),
                    }}
                  />
                  <Segment attached>
                    <IdeaImpactStatement
                      forStage="userEstimate"
                      editable={isEditing}
                      statementDefintion={challenge?.impactStatements}
                      idea={idea}
                      onUpdate={() => {
                        // Slight hack to force an idea update to reload submissibility status
                        // @ts-ignore
                        updateIdea(idea._id, ["_id"]);
                      }}
                    />
                  </Segment>
                </IdeaSection>
                <Divider hidden />
              </>
            );
          }
          return (
            <div style={{ marginBottom: 30 }} key={section.id}>
              <IdeaSection
                className={
                  sectionIndex === firstAssessmentSectionIndex ?
                    "assessment-tour-step-4"
                  : undefined
                }
                id={section.name}
                ref={(ref) => setSectionRef?.(ref, section.id)}
                $isAssessment={section.type === "assessment"}
              >
                <IdeaSectionHeader
                  section={section}
                  assessment={assessmentState}
                  canViewEdi={canViewEdi}
                  canEdit={canEdit}
                  isAuthor={isAuthor}
                />
                <Segment attached>
                  {section.type === "businessProfile" ?
                    <IdeaBusinessProfile
                      idea={idea}
                      isEditing={isEditing}
                      updateIdea={updateIdea}
                      setFieldRef={setFieldRef}
                    />
                  : <Custom
                      idea={idea}
                      section={section}
                      isEditing={isEditing}
                      updateIdea={updateIdea}
                      externalChanges={externalChanges}
                      emptyRequiredField={props.emptyRequiredField}
                      setFieldRef={setFieldRef}
                      scrollToField={scrollToField}
                      scrollToSection={scrollToSection}
                    />
                  }
                </Segment>
              </IdeaSection>
            </div>
          );
        },
      )}

      {isAssessor ?
        <div style={{ marginBottom: 30 }}>
          {assessmentState?.isSubmitted ?
            <Label color="green" content="Assessment submitted" />
          : <TooltipButton
              style={{ marginLeft: 3 }}
              content={
                (
                  assessmentState?.conflictOfInterest?.hasConflictOfInterest ===
                  true
                ) ?
                  "Submit assessment with conflict of interest"
                : "Submit assessment"
              }
              primary
              onClick={submitAssessment}
              disabled={!assessmentIsSubmissable}
              tooltip={
                assessmentIsSubmissable ? undefined : (
                  "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest"
                )
              }
              loading={isSavingAssessment}
            />
          }
        </div>
      : null}

      {!theme.sizes.isComputer ?
        <SideNavContainer style={{ marginBottom: 20 }}>
          <Header>Completion</Header>
          {ownerIdeaTemplate?.body?.map(
            (
              section: NonNullable<
                OpenAPI.Schemas["IdeaTemplate"]["body"]
              >[number],
              idx: number,
            ) => {
              const sectionName = section.name ?? "";
              if (!util.shouldShowIdeaSection(idea, section)) {
                return null;
              }
              if (section.type === "assessment" && !assessmentState) {
                return null;
              }
              const submissibility =
                idea.submissibilityStatus?.detail?.[section.id];

              return (
                <SideNavLink
                  key={`${sectionName}-${idx}`}
                  content={
                    sectionName.charAt(0).toUpperCase() + sectionName.slice(1)
                  }
                  rightIcon={
                    submissibility ?
                      <CircularProgress
                        size={16}
                        color={
                          (
                            submissibility.completedFields ===
                            submissibility.visibleFields
                          ) ?
                            "#28a745"
                          : submissibility.incorrectFields?.length > 0 ?
                            "#dc3545"
                          : "#007bff"
                        }
                        progress={
                          submissibility.completedFields /
                          submissibility.visibleFields
                        }
                        strokeWidth={1.5}
                        icon={<Icon name="check" color="green" />}
                        iconVisible={
                          submissibility.completedFields ===
                          submissibility.visibleFields
                        }
                      />
                    : undefined
                  }
                  onClick={() => scrollToSection?.(section.id)}
                />
              );
            },
          )}
        </SideNavContainer>
      : null}

      {parent || isAssessor || idea?.children ? null : (
        <SubmitIdeaButton
          idea={idea}
          updateIdea={updateIdea}
          onSubmit={onSubmit}
          scrollToRequired={scrollToRequired}
          isSaving={isSaving}
          unsaved={unsaved}
        />
      )}

      {showAttachments ?
        <div>
          <div
            ref={(ref) => setSectionRef?.(ref, "attachments")}
            id="attachments"
          >
            <Divider section hidden />
            <IdeaAttachments
              idea={idea}
              user={user}
              isEditing={isEditing}
              updateIdea={updateIdea}
            />
          </div>
        </div>
      : null}
      {showComments ?
        <div>
          <div
            ref={(ref) => setSectionRef?.(ref, "comments")}
            id="idea-comments"
          >
            <Divider hidden />
            <Header as="h4">{t("generic.comments")}</Header>
            <IdeaComments idea={idea} forId={idea._id} />
          </div>
        </div>
      : null}
    </>
  );
};

export default BodyContainer;
